export function onlyUnique<T>(value: T, index: number, self: T[]) {
  return self.indexOf(value) === index;
}

export function unique<T>(array: T[]): T[] {
  return array.filter(onlyUnique);
}

type NonArray<T> = T extends any[] ? never : T;

export function flatten<T>(list: T[]): NonArray<T>[] {
  return list.reduce(
    (acc: T[], val: T) =>
      Array.isArray(val) ? acc.concat(flatten(val)) : acc.concat(val),
    []
  ) as NonArray<T>[];
}

export function swapArrayElements(
  array: any[],
  indexA: number,
  indexB: number
) {
  const temp = array[indexA];
  array[indexA] = array[indexB]; // eslint-disable-line no-param-reassign
  array[indexB] = temp; // eslint-disable-line no-param-reassign
}

export function toggleElement<T>(array: T[], element: T): T[] {
  if (array.includes(element)) {
    return array.filter(i => i !== element);
  }
  return array.concat(element);
}

// Combine multiple compare functions for sorting, see Array.sort()
export function compareBy<T>(
  compareFns: ((a: T, b: T) => number)[]
): (a: T, b: T) => number {
  return (a: T, b: T): number => {
    let i = 0,
      result = 0;
    do {
      result = compareFns[i](a, b);
      i++;
    } while (result === 0 && i < compareFns.length);
    return result;
  };
}

export function lastItem<T>(array: T[]): T {
  return array[array.length - 1];
}

export function sum(values: number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

export function notEmpty<TValue>(
  value: TValue | null | undefined
): value is TValue {
  return value !== null && value !== undefined;
}

export function partition<T>(arr: T[], isValid: (e: T) => boolean): Array<T[]> {
  const pass: T[] = [];
  const fail: T[] = [];
  arr.forEach(e => (isValid(e) ? pass.push(e) : fail.push(e)));
  return [pass, fail];
}

export async function asyncForEach<T>(
  array: T[],
  callback: (e: T, i: number, a: T[]) => Promise<void>
): Promise<void> {
  for (let i = 0; i < array.length; i++) {
    await callback(array[i], i, array);
  }
}
